import React, { createContext, useContext, useState, ReactNode } from "react";

import { api, apiLogin, setAuthToken } from "../services/api";
import { COLLECTION_ACCESS, COLLECTION_CARTAO, COLLECTION_USERS, COLLECTION_TOKEN } from "../configs/database";
import { useEffect } from "react";
import { useWindowDimensions, sizesNumber } from "../utils/responsive";

type User = {
  matricula: string;
  nome?: string;
  email?: string;
  nomedependente?: string;
  permissao?: string;
  permissoes?: Array<any>;
  loja?: string;
  admin?: boolean;
  titular: "N" | "S";
  firstAccess: boolean;

};

type AuthContextData = {
  user: User;
  cartao: string;
  loading: boolean;
  isOpenMenu: boolean;
  acessos: ResAcessos;
  toggleMenu: () => void;
  getAccess: (numCartao: string, matricula: string, acessosDoLogin?: ResAcessos) => Promise<void>;
  changeUser: (associado: User) => void;
  attEmail: (email: string) => void;
  signIn: (login: string, password: string, matricula: string) => Promise<void>;
  signOut: () => Promise<void>;
  disableFirstAccess: () => void;
};

type AuthProviderProps = {
  children: ReactNode;
};

interface ResAcessos {
  cartao: string;
  nomedependente: string;
  tipodependente: string;
  acessos: Acesso[];
}

interface Acesso {
  associado: Associado[];
}

interface Associado {
  matricula: string;
  loja: string;
  nome: string;
  permissoes: Permissao[];
}

interface Permissao {
  descricao: string;
  permissao: string;
  codigo: string;
}

export const AuthContext = createContext({} as AuthContextData);

function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User>({} as User);
  const [cartao, setCartao] = useState('');
  const [acessos, setAcessos] = useState({} as ResAcessos);
  const [loading, setLoading] = useState(false);
  const [isOpenMenu, setIsOpenMenu] = useState(true);
  const {width} = useWindowDimensions(); 
  const [firstResponsiveMenu, setfirstResponsiveMenu] = useState(true);
  useEffect(() => {
    loadUserStorageData();
  }, []);

  useEffect(() => {
    if( (width <= sizesNumber.tablet)){
      if(isOpenMenu ) setIsOpenMenu(false);
      setfirstResponsiveMenu(false)
    } 

  }, [width]);

  async function loadUserStorageData() {
    const storageUser = localStorage.getItem(COLLECTION_USERS);
    const storageCartao = localStorage.getItem(COLLECTION_CARTAO);
    const storageAcessos = localStorage.getItem(COLLECTION_ACCESS);
    const storageToken = localStorage.getItem(COLLECTION_TOKEN);

    if (storageUser) {
      const userLogged = JSON.parse(storageUser) as User;
      const userCartao = storageCartao;

      if (storageAcessos) {
        const userAccess = JSON.parse(storageAcessos) as ResAcessos;
        setAcessos(userAccess);
      }
      if (storageToken) {
        setAuthToken(storageToken);
      }

      setUser(userLogged);
      setCartao(userCartao);
    }
  }

  async function signIn(login: string, password: string, matricula: string) {
    let titular: User["titular"];
    let acessosDoLogin: ResAcessos | undefined;
    try {
      setLoading(true);
      const res = await apiLogin.post<User & { token: string; acessosDependente?: ResAcessos }>("login", {numcartao: login, password: password});
      // acessosDependente (so de dependente) vai para o getAccess, nao para o
      // usuario gravado: num representante sao centenas de associados.
      const { acessosDependente, ...dadosUsuario } = res.data
      acessosDoLogin = acessosDependente
      setUser(dadosUsuario)
      localStorage.setItem(COLLECTION_USERS, JSON.stringify(dadosUsuario))
      setCartao(login)
      localStorage.setItem(COLLECTION_CARTAO, login)
      localStorage.setItem(COLLECTION_TOKEN, res.data.token)
      setAuthToken(res.data.token)
      titular = res.data.titular
    } catch (e) {
      throw new Error("Erro ao realizar login, confira sua senha e tente novamente.");
    } finally {
      setLoading(false);
    }

    // A senha ja foi aceita aqui: falha nos acessos do dependente nao e
    // "senha incorreta". Antes caia na mensagem acima e deixava a sessao
    // gravada, com a Home aberta e o menu vazio (cartao Representante
    // 000027, 30/09/2026).
    if (titular !== 'S') {
      try {
        await getAccess(login, matricula, acessosDoLogin)
      } catch (e) {
        await signOut()
        throw new Error("Não foi possível carregar seus acessos. Tente novamente.");
      }
    }
  }

  function changeUser(associado) {
    const storageUser = localStorage.getItem(COLLECTION_USERS);
    const userLogged = JSON.parse(storageUser) as User;
    const newUser = {...userLogged, matricula: associado.matricula, loja: associado.loja, nome: associado.nome ? associado.nome : user.nome, permissoes: associado.permissoes}
    setUser(newUser)
    localStorage.setItem(COLLECTION_USERS, JSON.stringify(newUser))
  }

  function attEmail(email) {
    const storageUser = localStorage.getItem(COLLECTION_USERS);
    const userLogged = JSON.parse(storageUser) as User;
    const newUser = {...userLogged, email}
    setUser(newUser)
    localStorage.setItem(COLLECTION_USERS, JSON.stringify(newUser))
  }

  // acessosDoLogin: a resposta do getAcessos que o proprio login ja trouxe
  // (acessosDependente). So consulta o Protheus de novo se ela nao vier.
  async function getAccess(numCartao, matricula, acessosDoLogin?: ResAcessos) {
    try {
      setLoading(true);
      const dados = acessosDoLogin
        ?? (await api.get<ResAcessos>("getAcessos", {params: { NUMCARTAO: numCartao.trim() }})).data;
      setAcessos(dados)
      localStorage.setItem(COLLECTION_ACCESS, JSON.stringify(dados))
      // Matricula digitada na tela de login; se ela nao estiver entre os
      // associados (ex. digitada em minusculas), abre no primeiro da lista.
      const associados = dados.acessos[0].associado
      const userDefault = associados.find(acess => acess.matricula.trim() === matricula.trim().toUpperCase()) ?? associados[0]
      changeUser(userDefault)
    } catch (e) {
      throw new Error("Erro ao carregar acessos");
    } finally {
      setLoading(false);
    }
  }

  // Le do localStorage (como attEmail), nao do estado "user": a tela Trocar
  // Senha chama attEmail e disableFirstAccess em sequencia, e o "user" daqui
  // ainda e o de antes do attEmail -- regravava o e-mail antigo por cima do novo.
  function disableFirstAccess() {
    const storageUser = localStorage.getItem(COLLECTION_USERS);
    const userLogged = JSON.parse(storageUser) as User;
    const newUser = {...userLogged, firstAccess: false}
    setUser(newUser)
    localStorage.setItem(COLLECTION_USERS, JSON.stringify(newUser))
  }

  async function signOut() {
    setUser({} as User);
    setCartao('');
    setAcessos({} as ResAcessos);
    localStorage.removeItem(COLLECTION_USERS);
    localStorage.removeItem(COLLECTION_CARTAO);
    localStorage.removeItem(COLLECTION_ACCESS);
    localStorage.removeItem(COLLECTION_TOKEN);
    setAuthToken(null);
  }

  function toggleMenu() {
    if(!firstResponsiveMenu || (width >= sizesNumber.tablet)) setIsOpenMenu(!isOpenMenu);
  }

  return (
    <AuthContext.Provider
      value={{ user, cartao, loading, isOpenMenu, acessos, toggleMenu, signIn, signOut, changeUser, getAccess, disableFirstAccess,attEmail }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);

  return context;
}

export { AuthProvider, useAuth };
